<template>
  <div class="vb-root" ref="root" :style="{ height: (STAGE_H * scale) + 'px' }">
    <div class="vb-stage" :style="{ transform: 'translateX(' + offsetX + 'px) scale(' + scale + ')' }">
      <!-- Broadcast side view of the court: one flat SVG where every point
           goes through the same perspective projection of the floor (proj),
           heights (net, players, ball) scaled by the depth. The camera sits
           slightly on the home side, so the net shows as a thin plane. -->
      <svg class="vb-scene" :viewBox="'0 0 ' + STAGE_W + ' ' + STAGE_H" preserveAspectRatio="none">
        <defs>
          <pattern v-if="crowd" :id="uid + 'crowd'" patternUnits="userSpaceOnUse" width="90" height="45">
            <image :href="crowd" x="0" y="0" width="90" height="45" />
          </pattern>
          <linearGradient :id="uid + 'free'" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stop-color="#255f9e" />
            <stop offset="1" stop-color="#1b4b82" />
          </linearGradient>
          <linearGradient :id="uid + 'court'" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stop-color="#e38543" />
            <stop offset="1" stop-color="#c9682c" />
          </linearGradient>
          <!-- Serve fan, in the fan's own coordinates (apex at 0,0). -->
          <radialGradient :id="uid + 'fan'" cx="0" cy="0" :r="FAN_R" gradientUnits="userSpaceOnUse">
            <stop offset="0" stop-color="#8fd6ff" stop-opacity="0.4" />
            <stop offset="1" stop-color="#6cc3f5" stop-opacity="0" />
          </radialGradient>
          <radialGradient :id="uid + 'ping'" cx="0" cy="0" :r="FAN_R" gradientUnits="userSpaceOnUse">
            <stop offset="0" stop-color="#b5e8ff" stop-opacity="0.9" />
            <stop offset="0.55" stop-color="#7fd0ff" stop-opacity="0.5" />
            <stop offset="1" stop-color="#6cc3f5" stop-opacity="0.05" />
          </radialGradient>
          <radialGradient :id="uid + 'ball'" cx="0.35" cy="0.35" r="0.75">
            <stop offset="0" stop-color="#fff6c4" />
            <stop offset="0.55" stop-color="#ffd23a" />
            <stop offset="1" stop-color="#d99a00" />
          </radialGradient>
        </defs>

        <!-- Stands. -->
        <rect x="0" y="0" :width="STAGE_W" :height="STAGE_H" fill="#15151c" />
        <rect v-if="crowd" x="0" y="0" :width="STAGE_W" :height="STAGE_H" :fill="'url(#' + uid + 'crowd)'" opacity="0.8" />

        <!-- Free zone, the court, then the dark foreground band. -->
        <polygon :points="freeZone" :fill="'url(#' + uid + 'free)'" />
        <polygon :points="court" :fill="'url(#' + uid + 'court)'" />
        <rect x="0" :y="FRONT_Y" :width="STAGE_W" :height="STAGE_H - FRONT_Y" fill="#1a2236" />
        <line x1="0" :y1="FRONT_Y" :x2="STAGE_W" :y2="FRONT_Y" stroke="rgba(255,255,255,0.3)" stroke-width="1" />

        <!-- Barriers around the free zone. -->
        <ScrollingBoards :boards="boards" :uid="uid" :logo-url="logoUrl" :board-text="boardText" fill="#13306b" top-fill="#1f438a" />

        <!-- Logos painted on the free zone along the near sideline, one per
             half (the net post is in the middle). -->
        <template v-if="showSirplayLogo"><image v-for="(t, i) in logoDecals" :key="'logo' + i" :href="PITCH_LOGO" :transform="t" x="-180" y="-50" width="360" height="100" preserveAspectRatio="none" class="arena-logo" /></template>

        <!-- Serve fan: from the server toward the other half (hidden while
             a point is being played), with two radar-like "ping" waves. -->
        <g v-if="fan && !rally && !finished" :transform="'translate(' + pt(fan.apex) + ')'">
          <path :d="fan.d" :fill="'url(#' + uid + 'fan)'" />
          <g v-for="w in 2" :key="'ping' + w" opacity="0">
            <animateTransform attributeName="transform" type="scale" values="0.08;1" keyTimes="0;1" calcMode="spline" keySplines="0.2 0.7 0.4 1" dur="1.8s" :begin="(w - 1) * 0.9 + 's'" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0;1;0.9;0" keyTimes="0;0.12;0.7;1" dur="1.8s" :begin="(w - 1) * 0.9 + 's'" repeatCount="indefinite" />
            <path :d="fan.d" :fill="'url(#' + uid + 'ping)'" />
          </g>
        </g>

        <!-- Court lines: boundary, centre line, attack lines. -->
        <g fill="none" stroke="rgba(255,255,255,0.95)" stroke-width="1.3" stroke-linejoin="miter">
          <path v-for="(d, i) in courtLines" :key="'l' + i" :d="d" />
        </g>

        <!-- Where the ball hit the floor. -->
        <g v-if="rally">
          <ellipse v-for="b in rally.bounces" :key="b.key" :cx="b.x" :cy="b.y" :rx="7 * b.k" :ry="2.4 * b.k" class="vb-bounce" />
        </g>

        <!-- First referee on the stand by the far post. -->
        <g>
          <path :d="referee.stand" stroke="#9ca3af" stroke-width="1.6" fill="none" />
          <polygon :points="referee.platform" fill="#4b5563" />
        </g>

        <!-- Away half (behind the net from this camera), the net, then the
             home half in front of it. -->
        <g v-for="f in figuresBehind" :key="f.key" class="vb-fig" :class="{ 'is-held': f.held }" :style="f.style">
          <ellipse cx="0" cy="0" :rx="6 * f.shadow" :ry="1.8 * f.shadow" fill="rgba(0,0,0,0.3)" />
          <g :transform="'translate(0,' + (-f.jump).toFixed(1) + ')'">
            <g class="vb-body" :style="{ animationDelay: f.delay }">
              <path d="M-1.5,-12.5 L-1.8,0 M1.5,-12.5 L1.8,0" :stroke="f.skin" stroke-width="2.4" stroke-linecap="round" fill="none" />
              <path d="M-1.7,-8 L-1.7,-6.2 M1.7,-8 L1.7,-6.2" stroke="#e5e7eb" stroke-width="2.8" fill="none" />
              <ellipse cx="-1.9" cy="0" rx="1.7" ry="0.8" fill="#f8fafc" />
              <ellipse cx="1.9" cy="0" rx="1.7" ry="0.8" fill="#f8fafc" />
              <rect x="-3.3" y="-14.8" width="6.6" height="3.8" rx="0.8" :fill="f.color" />
              <rect x="-3.3" y="-14.8" width="6.6" height="3.8" rx="0.8" fill="rgba(0,0,0,0.45)" />
              <rect x="-3.6" y="-22.6" width="7.2" height="8.6" rx="1.8" :fill="f.color" stroke="rgba(0,0,0,0.25)" stroke-width="0.4" />
              <path :d="ARMS[f.pose]" :stroke="f.skin" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" fill="none" />
              <circle cx="0" cy="-25.2" r="2.6" :fill="f.skin" />
              <path d="M-2.6,-25.5 A2.6,2.6 0 0 1 2.6,-25.5 Z" :fill="f.hair" />
            </g>
          </g>
        </g>

        <g v-if="ballView && ballView.behind">
          <ellipse :cx="ballView.shadow.x" :cy="ballView.shadow.y" :rx="3 * ballView.k" :ry="1.1 * ballView.k" fill="rgba(0,0,0,0.3)" :opacity="ballView.opacity" />
          <g :transform="ballView.transform" :opacity="ballView.opacity">
            <circle r="3" :fill="'url(#' + uid + 'ball)'" />
            <path d="M-3,0 Q0,-1.9 3,0 M-1.1,-2.8 Q0.9,0 -1.1,2.8" stroke="#1d4ed8" stroke-width="0.8" fill="none" />
          </g>
        </g>

        <!-- Net: posts, cables, mesh with top / bottom tapes, antennas. -->
        <g stroke-linecap="round">
          <path :d="net.posts" stroke="#d1d5db" stroke-width="2.4" fill="none" />
          <path :d="net.cables" stroke="rgba(226,232,240,0.6)" stroke-width="0.6" fill="none" />
          <polygon :points="net.mesh" fill="rgba(15,23,42,0.3)" />
          <path :d="net.grid" stroke="rgba(15,23,42,0.55)" stroke-width="0.35" fill="none" />
          <path :d="net.bottom" stroke="#f8fafc" stroke-width="0.9" fill="none" />
          <path :d="net.top" stroke="#fff" stroke-width="2.2" fill="none" />
          <path :d="net.antennas" stroke="#fff" stroke-width="1.3" fill="none" />
          <path :d="net.antennas" stroke="#dc2626" stroke-width="1.3" stroke-dasharray="2.2 2.2" fill="none" />
        </g>

        <g v-for="f in figuresFront" :key="f.key" class="vb-fig" :class="{ 'is-held': f.held }" :style="f.style">
          <ellipse cx="0" cy="0" :rx="6 * f.shadow" :ry="1.8 * f.shadow" fill="rgba(0,0,0,0.3)" />
          <g :transform="'translate(0,' + (-f.jump).toFixed(1) + ')'">
            <g class="vb-body" :style="{ animationDelay: f.delay }">
              <path d="M-1.5,-12.5 L-1.8,0 M1.5,-12.5 L1.8,0" :stroke="f.skin" stroke-width="2.4" stroke-linecap="round" fill="none" />
              <path d="M-1.7,-8 L-1.7,-6.2 M1.7,-8 L1.7,-6.2" stroke="#e5e7eb" stroke-width="2.8" fill="none" />
              <ellipse cx="-1.9" cy="0" rx="1.7" ry="0.8" fill="#f8fafc" />
              <ellipse cx="1.9" cy="0" rx="1.7" ry="0.8" fill="#f8fafc" />
              <rect x="-3.3" y="-14.8" width="6.6" height="3.8" rx="0.8" :fill="f.color" />
              <rect x="-3.3" y="-14.8" width="6.6" height="3.8" rx="0.8" fill="rgba(0,0,0,0.45)" />
              <rect x="-3.6" y="-22.6" width="7.2" height="8.6" rx="1.8" :fill="f.color" stroke="rgba(0,0,0,0.25)" stroke-width="0.4" />
              <path :d="ARMS[f.pose]" :stroke="f.skin" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" fill="none" />
              <circle cx="0" cy="-25.2" r="2.6" :fill="f.skin" />
              <path d="M-2.6,-25.5 A2.6,2.6 0 0 1 2.6,-25.5 Z" :fill="f.hair" />
            </g>
          </g>
        </g>

        <!-- Idle: the server bounces the ball on the floor before serving. -->
        <g v-if="idleBall">
          <ellipse :cx="idleBall.shadow.x" :cy="idleBall.shadow.y" :rx="3 * idleBall.k" :ry="1.1 * idleBall.k" fill="rgba(0,0,0,0.3)" class="vb-ball-idle-shadow" />
          <g class="vb-ball-idle" :style="{ '--drop': idleBall.drop + 'px' }">
            <g :transform="idleBall.transform">
              <circle r="3" :fill="'url(#' + uid + 'ball)'" />
              <path d="M-3,0 Q0,-1.9 3,0 M-1.1,-2.8 Q0.9,0 -1.1,2.8" stroke="#1d4ed8" stroke-width="0.8" fill="none" />
            </g>
          </g>
        </g>
        <g v-if="ballView && !ballView.behind">
          <ellipse :cx="ballView.shadow.x" :cy="ballView.shadow.y" :rx="3 * ballView.k" :ry="1.1 * ballView.k" fill="rgba(0,0,0,0.3)" :opacity="ballView.opacity" />
          <g :transform="ballView.transform" :opacity="ballView.opacity">
            <circle r="3" :fill="'url(#' + uid + 'ball)'" />
            <path d="M-3,0 Q0,-1.9 3,0 M-1.1,-2.8 Q0.9,0 -1.1,2.8" stroke="#1d4ed8" stroke-width="0.8" fill="none" />
          </g>
        </g>
      </svg>

      <!-- Flat overlays (HTML, so text renders crisply at every scale). -->
      <div class="vb-score">
        <span class="vb-score-set">{{ $t('statistics.set') }} {{ setNo }}</span>
        <span class="vb-score-games">{{ curGames.h }}-{{ curGames.a }}</span>
        <span class="vb-score-pts">
          <span :key="'h' + popKey" :class="{ 'is-pop': popSide === '1' }">{{ curH }}</span>
          <span :key="'a' + popKey" :class="{ 'is-pop': popSide === '2' }">{{ curA }}</span>
        </span>
        <span v-if="statusText" class="vb-score-status">{{ statusText }}</span>
      </div>
      <div class="vb-label vb-label--home" :style="labelStyle('1')">{{ homeName }}</div>
      <div class="vb-label vb-label--away" :style="labelStyle('2')">{{ awayName }}</div>
      <div v-if="(curServe === '1' || curServe === '2') && !finished" class="vb-serving">
        <span class="vb-serving-label">{{ $t('statistics.atserve') }}</span>
        <span class="vb-serving-name">{{ curServe === '1' ? homeName : awayName }}</span>
        <span class="vb-serving-bar" :style="{ background: sideColor(curServe) }"></span>
      </div>

      <!-- Time-out: a short banner over the court. -->
      <transition name="vb-flash">
        <div v-if="flash && !finished" :key="flash.key" class="vb-flash" :style="{ '--side-color': sideColor(flash.side) }">
          <v-icon size="16" color="#fff">mdi-timer-outline</v-icon>
          <span class="vb-flash-text">{{ flash.text }}</span>
          <span class="vb-flash-name">{{ flash.side === '1' ? homeName : awayName }}</span>
        </div>
      </transition>

      <!-- Point / ace / set won: full-stage overlay, then the score box updates. -->
      <transition name="vb-overlay">
        <div v-if="overlay" :key="overlay.key" class="vb-point-overlay" :style="{ '--side-color': overlay.color }">
          <div class="vb-point-title">{{ overlay.title }}</div>
          <div v-if="overlay.sub" class="vb-point-sub">{{ overlay.sub }}</div>
          <div class="vb-point-team">{{ overlay.name }}</div>
          <div class="vb-point-score">
            <span :class="{ 'is-won': overlay.side === '1' }">{{ overlay.h }}</span>
            <span class="vb-point-sep">-</span>
            <span :class="{ 'is-won': overlay.side === '2' }">{{ overlay.a }}</span>
          </div>
        </div>
      </transition>

      <!-- Match over: big badge that stays up. -->
      <transition name="vb-final">
        <div v-if="finished" class="vb-final">
          <div class="vb-final-badge">
            <svg class="vb-final-icon" viewBox="0 0 40 40">
              <path d="M12,5 H28 V15 a8,8 0 0 1 -16,0 Z" fill="#facc15" />
              <path d="M12,8 H6 a6,6 0 0 0 6,9 M28,8 H34 a6,6 0 0 1 -6,9" fill="none" stroke="#facc15" stroke-width="2.4" />
              <rect x="18" y="22" width="4" height="7" fill="#eab308" />
              <rect x="12" y="29" width="16" height="5" rx="1.5" fill="#eab308" />
            </svg>
            <div class="vb-final-title">{{ $t('statistics.matchfinished') }}</div>
            <div v-if="winnerName" class="vb-final-winner">{{ winnerName }}</div>
            <div v-if="games.length" class="vb-final-sets">
              <span v-for="(g, i) in games" :key="'fs' + i">{{ g.h }}-{{ g.a }}</span>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import ScrollingBoards from '@/components/arenamatchtracker/ScrollingBoards.vue';
import { makeProjection, makeBoard, pt, seg, crowdTexture, stageScaleMixin, floorLogo, PITCH_LOGO, sirplayLicenceMixin } from '@/services/matchtracker/broadcastScene';

// Stage = fixed broadcast frame, scaled to the container width and zoomed
// in by ZOOM (the extra width is cropped equally off both sides).
const ZOOM = 1.1;
const STAGE_W = 460;
const STAGE_H = 270;
// Court projection (see makeProjection): u along the 18m court (net at
// 0.5), v across its 9m. FAR_CX < NEAR_CX puts the camera on the home side.
const FAR_Y = 104;
const NEAR_Y = 176;
const FAR_W = 250;
const NEAR_W = 318;
const FAR_CX = 226;
const NEAR_CX = 234;
// Screen px per metre of height at the far sideline.
const PXM = FAR_W / 18;
// Free zone around the court, in court units.
const FZ = { u0: -0.3, u1: 1.3, v0: -0.5, v1: 1.5 };
// Net: 1m tall band topped at 2.43m (men), 10m long, posts 1m outside.
const NET_TOP = 2.43;
const NET_BOTTOM = 1.43;
const NET_V0 = -0.06;
const NET_V1 = 1.06;
const POST_V0 = -0.12;
const POST_V1 = 1.12;
const FRONT_Y = 230;
const FAN_R = 140;

// Rally timing (ms).
const T_TOSS = 800;
const T_SERVE = 1000;
const T_PASS = 750;
const T_SET = 650;
const T_ATTACK = 420;
const T_BOUNCE = 650;
const OVERLAY_MS = 2400;
const FLASH_MS = 3500;
// A set is won at 25 (the fifth at 15) with a 2-point lead.
const SET_POINTS = 25;
const TIEBREAK_POINTS = 15;
const TIEBREAK_SET = 5;

// Arms by pose, in the figure's own units (feet at 0,0, 1 unit = 1px at
// the far sideline).
const ARMS = {
  ready: 'M-3.3,-21.5 L-4.3,-17.2 L-2.4,-14.9 M3.3,-21.5 L4.3,-17.2 L2.4,-14.9',
  dig: 'M-3.3,-21.2 L-0.7,-15.4 M3.3,-21.2 L0.7,-15.4',
  up: 'M-3.2,-21.8 L-3.9,-26.6 L-3.2,-30.6 M3.2,-21.8 L3.9,-26.6 L3.2,-30.6',
  spike: 'M-3.2,-21.8 L-5.6,-26 L-6.3,-29.2 M3.2,-21.8 L4.4,-27 L4.1,-31.2'
};
const SKINS = ['#e0b896', '#c8966f', '#8d5f3f', '#f0c9a6', '#a9744f', '#d6a47e'];
const HAIRS = ['#2b1d14', '#5a3a1e', '#111111', '#8a5a2b', '#1c1410', '#3b2a1a'];
// Where each player stands, home side (away mirrored on u): 0-2 front row,
// 3-5 back row, far to near. 1 is the setter; the server is the back player
// on the right of the team as it faces the net.
const BASE = [
  { u: 0.40, v: 0.2 }, { u: 0.41, v: 0.5 }, { u: 0.40, v: 0.8 },
  { u: 0.16, v: 0.2 }, { u: 0.15, v: 0.5 }, { u: 0.16, v: 0.8 }
];
const SETTER = 1;
// Figures are drawn far to near so nearer ones cover farther ones.
const DRAW_ORDER = [0, 3, 1, 4, 2, 5];

const rand = (a, b) => a + Math.random() * (b - a);
const pick = list => list[Math.floor(Math.random() * list.length)];
const num = v => parseInt(v) || 0;
const clamp01 = x => Math.max(0, Math.min(1, x));
const easeOut = x => 1 - (1 - x) * (1 - x);
const other = s => (s === '1' ? '2' : '1');
// Home plays on u < 0.5, away on u > 0.5.
const mirror = (side, u) => (side === '1' ? u : 1 - u);
const basePos = (side, idx) => ({ u: mirror(side, BASE[idx].u), v: BASE[idx].v });
const serverIdx = side => (side === '1' ? 5 : 3);

const { proj } = makeProjection({ farY: FAR_Y, nearY: NEAR_Y, farW: FAR_W, nearW: NEAR_W, farCx: FAR_CX, nearCx: NEAR_CX });
// A point h metres above the floor.
const lift = (u, v, h) => {
  const g = proj(u, v);
  const k = g.w / FAR_W;
  return { x: g.x, y: g.y - h * PXM * k, k };
};
// Point where the screen line a -> b crosses x.
const atX = (a, b, x) => {
  const t = (x - a.x) / (b.x - a.x);
  return { x, y: a.y + (b.y - a.y) * t };
};
const hexRgb = hex => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  return [0, 2, 4].map(i => parseInt(m[1].slice(i, i + 2), 16));
};
const textOn = hex => {
  const rgb = hexRgb(hex);
  if (!rgb) return '#fff';
  const [r, g, b] = rgb.map(c => { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.55 ? '#111827' : '#fff';
};

export default {
  name: 'VolleyArena',
  components: { ScrollingBoards },
  mixins: [stageScaleMixin, sirplayLicenceMixin],
  props: {
    homeName: { type: String, default: '' },
    awayName: { type: String, default: '' },
    // Points in the set being played.
    pointsH: { type: String, default: '0' },
    pointsA: { type: String, default: '0' },
    // Sets won (the scoreboard).
    gamesH: { type: [String, Number], default: 0 },
    gamesA: { type: [String, Number], default: 0 },
    // '1' home / '2' away / null.
    servingSide: { type: String, default: null },
    logoUrl: { type: String, default: '' },
    boardText: { type: String, default: 'LIVE' },
    homeColor: { type: String, default: '' },
    awayColor: { type: String, default: '' },
    // Points per set so far, [{ h, a }] (for the final badge).
    games: { type: Array, default: () => [] },
    // Running totals of the volleyball statistics, { h, a } each:
    // aces (20), service errors (2010 / 2068), time-outs (31). They say how
    // a point was won (ace / service error) and trigger the time-out banner.
    aces: { type: Object, default: () => ({ h: 0, a: 0 }) },
    serviceErrors: { type: Object, default: () => ({ h: 0, a: 0 }) },
    timeouts: { type: Object, default: () => ({ h: 0, a: 0 }) },
    finished: { type: Boolean, default: false }
  },
  data() {
    return {
      PITCH_LOGO,
      STAGE_W,
      STAGE_H,
      ZOOM,
      FRONT_Y,
      FAN_R,
      ARMS,
      crowd: '',
      // Score shown on the court: lags the props while a point is animated.
      curH: this.pointsH,
      curA: this.pointsA,
      curGames: { h: num(this.gamesH), a: num(this.gamesA) },
      curServe: this.servingSide,
      // Stat totals already accounted for by a shown point.
      seenAces: { h: num(this.aces.h), a: num(this.aces.a) },
      seenErrors: { h: num(this.serviceErrors.h), a: num(this.serviceErrors.a) },
      rally: null,
      rallyRaf: null,
      overlay: null,
      overlayTimer: null,
      flash: null,
      flashTimer: null,
      flashKey: 0,
      popSide: null,
      popKey: 0,
      // The point that closed a set was already shown as "set won": the
      // sets update that follows is then synced without a second overlay.
      pendingGameWin: null
    };
  },
  watch: {
    pointKey(val, old) {
      if (!old || this.finished) {
        this.syncScore();
        return;
      }
      this.onScoreChange();
    },
    // A serve change with no point played (e.g. first feed update).
    servingSide(val) {
      if (!this.rally) this.curServe = val;
    },
    finished(val) {
      if (val) {
        this.stopRally();
        this.syncScore();
      }
    },
    timeouts(val, old) {
      if (!old) return;
      const side = num(val.h) > num(old.h) ? '1' : (num(val.a) > num(old.a) ? '2' : null);
      if (!side) return;
      this.flash = { key: ++this.flashKey, side, text: this.$t('statistics.timeouts') };
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => { this.flash = null; }, FLASH_MS);
    }
  },
  mounted() {
    this.crowd = crowdTexture('arena');
  },
  beforeDestroy() {
    this.stopRally();
    clearTimeout(this.overlayTimer);
    clearTimeout(this.flashTimer);
  },
  methods: {
    pt,
    syncScore() {
      this.curH = this.pointsH;
      this.curA = this.pointsA;
      this.curGames = { h: num(this.gamesH), a: num(this.gamesA) };
      this.curServe = this.servingSide;
      this.syncStats();
    },
    syncStats() {
      this.seenAces = { h: num(this.aces.h), a: num(this.aces.a) };
      this.seenErrors = { h: num(this.serviceErrors.h), a: num(this.serviceErrors.a) };
    },
    // How the point was won, from the stats that went up with it: 'ace'
    // (the server's aces), 'error' (the server's service errors) or 'rally'.
    pointKind(server, winner) {
      const key = server === '1' ? 'h' : 'a';
      const ace = num(this.aces[key]) > this.seenAces[key];
      const error = num(this.serviceErrors[key]) > this.seenErrors[key];
      this.syncStats();
      if (ace && winner === server) return 'ace';
      if (error && winner !== server) return 'error';
      return 'rally';
    },
    // Works out who won the point from the old/new score, plays the rally,
    // then shows the overlay and moves the score box on.
    onScoreChange() {
      const prev = this.curGames;
      const games = { h: num(this.gamesH), a: num(this.gamesA) };
      let winner = null;
      let kind = 'point';
      if (games.h !== prev.h || games.a !== prev.a) {
        kind = 'game';
        winner = games.h > prev.h ? '1' : (games.a > prev.a ? '2' : null);
        if (winner && winner === this.pendingGameWin) {
          this.pendingGameWin = null;
          this.syncScore();
          return;
        }
      } else {
        const o = { h: num(this.curH), a: num(this.curA) };
        const n = { h: num(this.pointsH), a: num(this.pointsA) };
        if (n.h > o.h && n.a >= o.a) winner = '1';
        else if (n.a > o.a && n.h >= o.h) winner = '2';
        // The point closes the set (25-x with a 2-point lead): show it as
        // a set won straight away.
        if (winner) {
          const w = winner === '1' ? n.h : n.a;
          const l = winner === '1' ? n.a : n.h;
          const closes = w >= this.setTarget && w - l >= 2;
          if (closes) kind = 'game';
          this.pendingGameWin = closes ? winner : null;
        }
      }
      if (!winner) {
        this.syncScore();
        return;
      }
      const server = this.curServe === '1' || this.curServe === '2' ? this.curServe : winner;
      const how = this.pointKind(server, winner);
      this.playRally(server, winner, how, () => this.showPoint(kind, winner, how));
    },
    showPoint(kind, winner, how) {
      this.syncScore();
      let h = this.curH;
      let a = this.curA;
      if (kind === 'game') {
        // Sets won, counting this one if the scoreboard hasn't caught up yet.
        const g = { ...this.curGames };
        if (this.pendingGameWin === winner) g[winner === '1' ? 'h' : 'a']++;
        h = g.h;
        a = g.a;
      }
      // An ace is announced as such, even when it closes the set (then
      // "Set" goes underneath).
      const ace = how === 'ace';
      let title = this.$t(kind === 'game' ? 'statistics.set' : 'statistics.point');
      if (ace) title = this.$t('statistics.ace');
      this.popKey++;
      this.popSide = winner;
      this.overlay = {
        key: this.popKey,
        side: winner,
        title,
        sub: ace && kind === 'game' ? this.$t('statistics.set') : '',
        name: winner === '1' ? this.homeName : this.awayName,
        color: this.sideColor(winner),
        h,
        a
      };
      clearTimeout(this.overlayTimer);
      this.overlayTimer = setTimeout(() => { this.overlay = null; }, OVERLAY_MS);
    },
    sideColor(side) {
      if (side === '1') return this.homeColor || '#2d5fae';
      return this.awayColor || '#1c8a8e';
    },
    labelStyle(side) {
      const c = this.sideColor(side);
      return { background: c, color: textOn(c) };
    },
    stopRally() {
      if (this.rallyRaf) cancelAnimationFrame(this.rallyRaf);
      this.rallyRaf = null;
      this.rally = null;
    },
    serveSpotFor(side) {
      return side === '1' ? { u: -0.07, v: 0.75 } : { u: 1.07, v: 0.25 };
    },
    // Closest player of `side` to the court point p, skipping `skip`.
    closest(side, p, skip = []) {
      let best = null;
      let bestD = Infinity;
      for (let i = 0; i < 6; i++) {
        if (skip.includes(i)) continue;
        const b = basePos(side, i);
        const d = Math.hypot(b.u - p.u, (b.v - p.v) * 0.5);
        if (d < bestD) { bestD = d; best = i; }
      }
      return best;
    },
    // Builds the flight segments of the point. Each segment may carry the
    // player who touches the ball at its end (who), and a blocker jumping
    // at the net while it flies (block). Heights in metres.
    buildRally(server, winner, how) {
      const segs = [];
      const spot = this.serveSpotFor(server);
      const dir = server === '1' ? 1 : -1;
      const sIdx = serverIdx(server);
      // Toss: up and back down to the jump-serve contact point, the server
      // stepping in and jumping.
      const hand = { u: spot.u + 0.015 * dir, v: spot.v, h: 1.1 };
      const contact = { u: spot.u + 0.04 * dir, v: spot.v, h: 3.3 };
      segs.push({
        from: hand, to: contact, peak: 1.8, dur: T_TOSS,
        who: { side: server, idx: sIdx, start: spot, u: spot.u + 0.035 * dir, v: spot.v + 0.01, pose: 'spike', jump: 0.7 }
      });
      if (how === 'error') {
        // Service error: into the net, or long past the far end line.
        const net = Math.random() < 0.5;
        const to = net
          ? { u: 0.5 - 0.012 * dir, v: rand(0.25, 0.75), h: rand(1.6, 2.2) }
          : { u: mirror(server, rand(1.06, 1.16)), v: rand(0.1, 0.9), h: 0 };
        segs.push({ from: contact, to, peak: net ? 0.4 : 2.4, dur: T_SERVE, land: !net });
        const drop = net ? { u: 0.5 - 0.05 * dir, v: to.v, h: 0 } : { u: to.u + 0.12 * dir, v: to.v, h: 0 };
        segs.push({ from: to, to: drop, peak: net ? 0.1 : 0.9, dur: T_BOUNCE, fade: true });
        return segs;
      }
      const crossings = how === 'ace' ? 1 : (winner === server ? pick([3, 3, 5]) : pick([2, 2, 4]));
      let from = contact;
      let side = server;
      let attacker = null;
      for (let i = 0; i < crossings; i++) {
        const last = i === crossings - 1;
        const recv = other(side);
        let to;
        let dig = null;
        if (last) {
          // Kill: the ball drops on the receiving side's floor.
          to = { u: mirror(recv, rand(0.08, 0.38)), v: rand(0.08, 0.92), h: 0 };
        } else {
          to = { u: mirror(recv, rand(0.1, 0.28)), v: rand(0.15, 0.85), h: 0.8 };
          dig = this.closest(recv, to, [SETTER]);
        }
        const flight = i === 0
          ? { from, to, peak: last ? 2.2 : 2.6, dur: T_SERVE }
          : { from, to, peak: 0.5, dur: T_ATTACK };
        if (dig !== null) flight.who = { side: recv, idx: dig, u: to.u, v: to.v + 0.015, pose: 'dig', jump: 0 };
        if (i > 0) {
          const bIdx = [0, 1, 2].reduce((b, j) => (Math.abs(BASE[j].v - from.v) < Math.abs(BASE[b].v - from.v) ? j : b), 0);
          flight.block = { side: recv, idx: bIdx, u: mirror(recv, 0.46), v: from.v };
        }
        flight.land = last;
        segs.push(flight);
        if (last) {
          const d = recv === '1' ? -1 : 1;
          const out = { u: to.u + d * rand(0.12, 0.25), v: Math.max(-0.4, Math.min(1.4, to.v + rand(-0.25, 0.25))), h: 0 };
          segs.push({ from: to, to: out, peak: 1.2, dur: T_BOUNCE, fade: true });
          break;
        }
        // Build-up on the receiving side: dig to the setter, set to a
        // front-row attacker, who jumps and spikes on the next crossing.
        const setPt = { u: mirror(recv, 0.44), v: rand(0.42, 0.58), h: 2.6 };
        segs.push({ from: to, to: setPt, peak: 2.8, dur: T_PASS, who: { side: recv, idx: SETTER, u: setPt.u, v: setPt.v + 0.015, pose: 'up', jump: 0.25 } });
        const choices = [0, 2].filter(j => j !== dig);
        attacker = pick(choices.length ? choices : [0, 2]);
        const hit = { u: mirror(recv, 0.42), v: attacker === 0 ? 0.14 : 0.86, h: 3.1 };
        segs.push({ from: setPt, to: hit, peak: 1.6, dur: T_SET, who: { side: recv, idx: attacker, u: hit.u - 0.02 * (recv === '1' ? 1 : -1), v: hit.v + 0.015, pose: 'spike', jump: 0.75 } });
        from = hit;
        side = recv;
      }
      return segs;
    },
    // Players for one frame of the rally: each at its place, except the
    // one reaching for the ball (who), the one who just touched it (still
    // landing) and the blocker.
    layout(s, t, prev) {
      const list = [];
      for (const side of ['1', '2']) {
        for (let idx = 0; idx < 6; idx++) {
          let pos = basePos(side, idx);
          let jump = 0;
          let pose = 'ready';
          let held = false;
          const is = w => w && w.side === side && w.idx === idx;
          if (is(s.who)) {
            const w = s.who;
            const start = w.start || pos;
            const p = easeOut(clamp01(t / 0.8));
            pos = { u: start.u + (w.u - start.u) * p, v: start.v + (w.v - start.v) * p };
            if (w.jump && t > 0.55) jump = Math.sin(((t - 0.55) / 0.45) * Math.PI / 2) * w.jump;
            pose = t > 0.4 ? w.pose : 'ready';
            held = true;
          } else if (prev && is(prev.who) && t < 0.35) {
            const w = prev.who;
            pos = { u: w.u, v: w.v };
            if (w.jump) jump = Math.cos((t / 0.35) * Math.PI / 2) * w.jump;
            pose = w.pose;
            held = true;
          } else if (is(s.block)) {
            const b = s.block;
            const p = easeOut(clamp01(t / 0.4));
            pos = { u: pos.u + (b.u - pos.u) * p, v: pos.v + (b.v - pos.v) * p };
            if (t > 0.2) jump = Math.sin(clamp01((t - 0.2) / 0.8) * Math.PI) * 0.8;
            pose = 'up';
            held = true;
          }
          list.push(this.figure(side, idx, pos, jump, pose, held));
        }
      }
      return list;
    },
    figure(side, idx, pos, jumpM, pose, held) {
      const p = lift(pos.u, pos.v, 0);
      const n = (side === '1' ? 0 : 6) + idx;
      return {
        key: 'p' + side + idx,
        side,
        idx,
        u: pos.u,
        held,
        pose,
        jump: jumpM * PXM,
        shadow: Math.max(0.5, 1 - jumpM * 0.5),
        color: this.sideColor(side),
        skin: SKINS[n % SKINS.length],
        hair: HAIRS[(n * 5) % HAIRS.length],
        delay: (-(n * 0.37) % 1.6).toFixed(2) + 's',
        style: { transform: `translate(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px) scale(${p.k.toFixed(3)})` }
      };
    },
    playRally(server, winner, how, done) {
      this.stopRally();
      this.curServe = server;
      const segs = this.buildRally(server, winner, how);
      let i = 0;
      let t0 = null;
      let spin = 0;
      let lastTs = null;
      const bounces = [];
      const frame = ts => {
        if (t0 === null) { t0 = ts; lastTs = ts; }
        let s = segs[i];
        let t = (ts - t0) / s.dur;
        while (t >= 1) {
          if (s.land) {
            const b = lift(s.to.u, s.to.v, 0);
            bounces.push({ key: ts + '-' + i, x: b.x, y: b.y, k: b.k });
            if (bounces.length > 3) bounces.shift();
          }
          i++;
          if (i >= segs.length) {
            this.stopRally();
            done();
            return;
          }
          t0 += s.dur;
          s = segs[i];
          t = (ts - t0) / s.dur;
        }
        const u = s.from.u + (s.to.u - s.from.u) * t;
        const v = s.from.v + (s.to.v - s.from.v) * t;
        const h = s.from.h + (s.to.h - s.from.h) * t + 4 * s.peak * t * (1 - t);
        const b = lift(u, v, Math.max(0, h));
        const sh = lift(u, v, 0);
        // Faster spin on quick (attack) flights.
        spin = (spin + (ts - lastTs) * (s.dur <= T_ATTACK ? 1.4 : 0.5)) % 360;
        lastTs = ts;
        const r = 3.2 * b.k;
        this.rally = {
          ball: {
            u,
            k: b.k,
            shadow: { x: sh.x + 1, y: sh.y + 0.5 },
            transform: `translate(${b.x.toFixed(1)},${(b.y - r).toFixed(1)}) scale(${(r / 3).toFixed(3)}) rotate(${spin.toFixed(0)})`,
            opacity: s.fade ? 1 - t : 1
          },
          players: this.layout(s, t, segs[i - 1]),
          bounces: bounces.slice()
        };
        this.rallyRaf = requestAnimationFrame(frame);
      };
      this.rallyRaf = requestAnimationFrame(frame);
    }
  },
  computed: {
    logoDecals() {
      return [0.25, 0.75].map(u => floorLogo(proj, u, 1 + 1.6 / 9, 0.9 / 9));
    },
    // SVG ids are document-global: keep each instance's gradients apart.
    uid() { return 'vb' + this._uid + '-'; },
    pointKey() {
      return [this.pointsH, this.pointsA, num(this.gamesH), num(this.gamesA)].join('|');
    },
    setNo() {
      return Math.min(TIEBREAK_SET, this.curGames.h + this.curGames.a + 1);
    },
    setTarget() {
      return num(this.gamesH) + num(this.gamesA) + 1 >= TIEBREAK_SET ? TIEBREAK_POINTS : SET_POINTS;
    },
    winnerName() {
      const h = num(this.gamesH);
      const a = num(this.gamesA);
      if (h === a) return '';
      return h > a ? this.homeName : this.awayName;
    },
    statusText() {
      const h = num(this.curH);
      const a = num(this.curA);
      const top = this.setTarget - 1;
      if ((h >= top || a >= top) && h !== a) return this.$t('statistics.setpoint');
      return '';
    },
    freeZone() {
      return [[FZ.u0, FZ.v0], [FZ.u1, FZ.v0], [FZ.u1, FZ.v1], [FZ.u0, FZ.v1]].map(c => pt(proj(c[0], c[1]))).join(' ');
    },
    court() {
      return [[0, 0], [1, 0], [1, 1], [0, 1]].map(c => pt(proj(c[0], c[1]))).join(' ');
    },
    boards() {
      const farL = proj(FZ.u0, FZ.v0);
      const farR = proj(FZ.u1, FZ.v0);
      const sideL = atX(farL, proj(FZ.u0, FZ.v1), 0);
      const sideR = atX(farR, proj(FZ.u1, FZ.v1), STAGE_W);
      return [
        makeBoard('far', farL, farR, 14, 14, 5, 40),
        makeBoard('left', sideL, farL, 22, 14, 2, 38),
        makeBoard('right', farR, sideR, 14, 22, 2, 38)
      ];
    },
    courtLines() {
      const e = 0.003;
      const lines = ['M' + [proj(e, e), proj(1 - e, e), proj(1 - e, 1 - e), proj(e, 1 - e)].map(pt).join(' L') + ' Z'];
      [0.5, 1 / 3, 2 / 3].forEach(u => lines.push(seg(proj(u, e), proj(u, 1 - e))));
      return lines;
    },
    net() {
      const line = (v0, v1, h) => seg(lift(0.5, v0, h), lift(0.5, v1, h));
      const grid = [];
      for (let v = NET_V0; v <= NET_V1 + 1e-6; v += 0.04) grid.push(seg(lift(0.5, v, NET_BOTTOM), lift(0.5, v, NET_TOP)));
      [1.68, 1.93, 2.18].forEach(h => grid.push(line(NET_V0, NET_V1, h)));
      return {
        mesh: [lift(0.5, NET_V0, NET_BOTTOM), lift(0.5, NET_V1, NET_BOTTOM), lift(0.5, NET_V1, NET_TOP), lift(0.5, NET_V0, NET_TOP)].map(pt).join(' '),
        grid: grid.join(' '),
        top: line(NET_V0, NET_V1, NET_TOP),
        bottom: line(NET_V0, NET_V1, NET_BOTTOM),
        posts: [POST_V0, POST_V1].map(v => seg(lift(0.5, v, 0), lift(0.5, v, NET_TOP + 0.15))).join(' '),
        cables: [
          seg(lift(0.5, POST_V0, NET_TOP), lift(0.5, NET_V0, NET_TOP)),
          seg(lift(0.5, POST_V0, NET_BOTTOM), lift(0.5, NET_V0, NET_BOTTOM)),
          seg(lift(0.5, NET_V1, NET_TOP), lift(0.5, POST_V1, NET_TOP)),
          seg(lift(0.5, NET_V1, NET_BOTTOM), lift(0.5, POST_V1, NET_BOTTOM))
        ].join(' '),
        antennas: [0, 1].map(v => seg(lift(0.5, v, NET_BOTTOM), lift(0.5, v, NET_TOP + 0.8))).join(' ')
      };
    },
    referee() {
      const v = -0.24;
      const H = 1.55;
      return {
        stand: seg(lift(0.47, v, 0), lift(0.47, v, H)) + ' ' + seg(lift(0.53, v, 0), lift(0.53, v, H)),
        platform: [lift(0.465, v - 0.03, H), lift(0.535, v - 0.03, H), lift(0.535, v + 0.03, H), lift(0.465, v + 0.03, H)].map(pt).join(' '),
        pos: { u: 0.5, v, h: H }
      };
    },
    refereeFigure() {
      const r = this.referee.pos;
      const p = lift(r.u, r.v, r.h);
      return {
        key: 'ref',
        held: true,
        pose: 'ready',
        jump: 0,
        shadow: 0,
        color: '#111827',
        skin: '#d6a47e',
        hair: '#3b3b3b',
        delay: '0s',
        style: { transform: `translate(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px) scale(${p.k.toFixed(3)})` }
      };
    },
    // Idle: everyone at their place, the server behind the end line.
    idlePlayers() {
      const list = [];
      const server = this.curServe === '1' || this.curServe === '2' ? this.curServe : null;
      for (const side of ['1', '2']) {
        for (let idx = 0; idx < 6; idx++) {
          const pos = server === side && idx === serverIdx(side) && !this.finished ? this.serveSpotFor(side) : basePos(side, idx);
          list.push(this.figure(side, idx, pos, 0, 'ready', false));
        }
      }
      return list;
    },
    players() {
      return this.rally ? this.rally.players : this.idlePlayers;
    },
    ordered() {
      const bySide = side => DRAW_ORDER.map(i => this.players.find(p => p.side === side && p.idx === i)).filter(Boolean);
      return { home: bySide('1'), away: bySide('2') };
    },
    // Behind the net from this camera: the referee and the away team.
    figuresBehind() {
      return [this.refereeFigure, ...this.ordered.away];
    },
    figuresFront() {
      return this.ordered.home;
    },
    ballView() {
      if (!this.rally) return null;
      return { ...this.rally.ball, behind: this.rally.ball.u > 0.5 };
    },
    serveSpot() {
      if (this.curServe !== '1' && this.curServe !== '2') return null;
      return this.serveSpotFor(this.curServe);
    },
    idleBall() {
      const s = this.serveSpot;
      if (!s || this.rally || this.finished) return null;
      const dir = this.curServe === '1' ? 1 : -1;
      const u = s.u + 0.02 * dir;
      const v = s.v + 0.02;
      const b = lift(u, v, 1.05);
      const sh = lift(u, v, 0);
      const r = 3.2 * b.k;
      return {
        k: b.k,
        shadow: { x: sh.x + 1, y: sh.y + 0.5 },
        drop: sh.y - b.y,
        transform: `translate(${b.x.toFixed(1)},${(b.y - r).toFixed(1)}) scale(${(r / 3).toFixed(3)})`
      };
    },
    fan() {
      const s = this.serveSpot;
      if (!s) return null;
      const apex = lift(s.u, s.v, 1.05);
      const t = proj(this.curServe === '1' ? 0.75 : 0.25, 0.5);
      const c = Math.atan2(t.y - apex.y, t.x - apex.x);
      const half = 16 * Math.PI / 180;
      // Path relative to the apex, so the ping can scale out of it.
      const p1 = { x: FAN_R * Math.cos(c - half), y: FAN_R * Math.sin(c - half) };
      const p2 = { x: FAN_R * Math.cos(c + half), y: FAN_R * Math.sin(c + half) };
      return {
        apex,
        d: `M0,0 L${pt(p1)} A${FAN_R},${FAN_R} 0 0 1 ${pt(p2)} Z`
      };
    }
  }
};
</script>

<style scoped>
.vb-root { position: relative; width: 100%; overflow: hidden; background: #15151c; }
.vb-stage { position: absolute; top: 0; left: 0; width: 460px; height: 270px; transform-origin: 0 0; font-family: Roboto, Arial, sans-serif; }
.vb-scene { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }

/* Players: slide back to their place when released; the one on the ball
   follows the rally frame by frame. */
.vb-fig { transition: transform 0.6s cubic-bezier(.3, .7, .3, 1); }
.vb-fig.is-held { transition: none; }
.vb-body { animation: vb-bob 1.6s ease-in-out infinite; }
@keyframes vb-bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(0.8px); } }

.vb-score { position: absolute; top: 8px; left: 0; right: 0; display: flex; justify-content: center; align-items: stretch; height: 22px; z-index: 2; }
.vb-score-set { display: flex; align-items: center; padding: 0 7px; background: #0f172a; color: #94a3b8; font-weight: 700; font-size: var(--font-size-xl); text-transform: uppercase; white-space: nowrap; }
.vb-score-games { display: flex; align-items: center; padding: 0 7px; background: #1f2937; color: #cbd5e1; font-weight: 700; font-size: var(--font-size-2xl); }
.vb-score-pts { display: flex; align-items: center; gap: 12px; padding: 0 7px; background: #0b0b0b; color: #fff; font-weight: 700; font-size: var(--font-size-5xl); line-height: 1; }
.vb-score-status { display: flex; align-items: center; padding: 0 8px; background: rgba(12, 20, 18, 0.72); color: #e9e33b; font-weight: 700; font-size: var(--font-size-2xl); white-space: nowrap; }

.vb-label { position: absolute; top: 40px; padding: 4px 8px; border-radius: 5px; font-weight: 700; font-size: var(--font-size-3xl); line-height: 1.2; white-space: nowrap; max-width: 150px; overflow: hidden; text-overflow: ellipsis; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35); z-index: 2; }
.vb-label--home { left: 30px; }
.vb-label--away { right: 30px; }

.vb-serving { position: absolute; left: 0; right: 0; top: 242px; display: flex; justify-content: center; align-items: center; gap: 7px; font-size: var(--font-size-3xl); z-index: 2; }
.vb-serving-label { color: #dbe7f1; font-weight: 600; }
.vb-serving-name { color: #fff; font-weight: 500; }
.vb-serving-bar { width: 2px; height: 15px; }

/* Idle: the server bounces the ball on the floor before serving. */
.vb-ball-idle { animation: vb-dribble 1.1s infinite; }
.vb-ball-idle-shadow { animation: vb-dribble-shadow 1.1s infinite; transform-box: fill-box; transform-origin: 50% 50%; }
@keyframes vb-dribble { 0%, 100% { transform: translateY(0); animation-timing-function: ease-in; } 50% { transform: translateY(var(--drop)); animation-timing-function: ease-out; } }
@keyframes vb-dribble-shadow { 0%, 100% { transform: scale(0.7); opacity: 0.6; } 50% { transform: scale(1); opacity: 1; } }

/* Mark left where the ball hit the floor. */
.vb-bounce { fill: none; stroke: rgba(255, 255, 255, 0.9); stroke-width: 1.2; transform-box: fill-box; transform-origin: 50% 50%; animation: vb-bounce 0.7s ease-out forwards; }
@keyframes vb-bounce { from { transform: scale(0.3); opacity: 1; } to { transform: scale(1.8); opacity: 0; } }

/* Score box: the number that just changed pops. */
.vb-score-pts .is-pop { display: inline-block; color: #e9e33b; animation: vb-pop 0.9s cubic-bezier(.2, 1.6, .4, 1) forwards; }
@keyframes vb-pop { 0% { transform: scale(0.4); } 40% { transform: scale(1.5); } 100% { transform: scale(1); color: #fff; } }

/* Time-out banner. */
.vb-flash { position: absolute; left: 50%; top: 188px; transform: translateX(-50%); z-index: 3; display: flex; align-items: center; gap: 7px; padding: 5px 12px; border-radius: 5px; border-left: 3px solid var(--side-color); background: rgba(11, 11, 11, 0.85); color: #fff; font-size: var(--font-size-3xl); white-space: nowrap; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.45); }
.vb-flash-text { font-weight: 700; }
.vb-flash-name { opacity: 0.85; }
.vb-flash-enter-active, .vb-flash-leave-active { transition: opacity 0.3s, transform 0.3s; }
.vb-flash-enter, .vb-flash-leave-to { opacity: 0; transform: translateX(-50%) translateY(8px); }

/* Point / ace / set overlay. */
.vb-point-overlay {
  position: absolute; inset: 0; z-index: 5; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px;
  background: radial-gradient(ellipse 70% 60% at 50% 50%, rgba(0, 0, 0, 0.35) 0%, rgba(0, 0, 0, 0.7) 100%);
  pointer-events: none;
}
.vb-point-overlay::before {
  content: ''; position: absolute; left: 0; right: 0; top: 50%; height: 96px; transform: translateY(-50%) skewY(-4deg);
  background: linear-gradient(90deg, transparent 0%, var(--side-color) 20%, var(--side-color) 80%, transparent 100%); opacity: 0.85;
  animation: vb-band 0.5s cubic-bezier(.2, .9, .3, 1) both;
}
.vb-point-title { position: relative; color: #fff; font-size: var(--font-size-17xl); font-weight: 900; font-style: italic; text-transform: uppercase; text-shadow: 0 3px 12px rgba(0, 0, 0, 0.55); animation: vb-title 0.6s cubic-bezier(.2, 1.5, .4, 1) 0.1s both; }
.vb-point-sub { position: relative; padding: 1px 10px; border-radius: 4px; background: #e9e33b; color: #0b0b0b; font-size: var(--font-size-3xl); font-weight: 900; text-transform: uppercase; animation: vb-rise 0.4s ease-out 0.2s both; }
.vb-point-team { position: relative; color: #fff; font-size: var(--font-size-4xl); font-weight: 700; opacity: 0.95; text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5); animation: vb-rise 0.4s ease-out 0.3s both; }
.vb-point-score { position: relative; display: flex; gap: 8px; margin-top: 4px; padding: 3px 12px; background: #0b0b0b; border-radius: 4px; color: #fff; font-size: var(--font-size-7xl); font-weight: 800; animation: vb-rise 0.4s ease-out 0.45s both; }
.vb-point-score .is-won { display: inline-block; color: #e9e33b; animation: vb-pop 0.9s cubic-bezier(.2, 1.6, .4, 1) 0.7s both; }
.vb-point-sep { opacity: 0.6; }
@keyframes vb-band { from { transform: translateY(-50%) skewY(-4deg) scaleX(0); } to { transform: translateY(-50%) skewY(-4deg) scaleX(1); } }
@keyframes vb-title { from { transform: scale(2.4); opacity: 0; } to { transform: scale(1); opacity: 1; } }
@keyframes vb-rise { from { transform: translateY(10px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.vb-overlay-enter-active { transition: opacity 0.2s; }
.vb-overlay-leave-active { transition: opacity 0.4s; }
.vb-overlay-enter, .vb-overlay-leave-to { opacity: 0; }

/* Match over. */
.vb-final { position: absolute; inset: 0; z-index: 6; display: flex; align-items: center; justify-content: center; background: rgba(5, 10, 16, 0.62); }
.vb-final-badge {
  display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 14px 34px 16px;
  background: linear-gradient(160deg, #1f2937 0%, #0b1220 100%); border: 2px solid #facc15; border-radius: 14px;
  box-shadow: 0 0 0 4px rgba(250, 204, 21, 0.15), 0 12px 30px rgba(0, 0, 0, 0.6);
  animation: vb-final-in 0.7s cubic-bezier(.2, 1.4, .4, 1) both;
}
.vb-final-icon { width: 40px; height: 40px; animation: vb-trophy 2s ease-in-out 0.7s infinite; }
.vb-final-title { color: #facc15; font-size: var(--font-size-10xl); font-weight: 900; text-transform: uppercase; white-space: nowrap; }
.vb-final-winner { color: #fff; font-size: var(--font-size-5xl); font-weight: 700; }
.vb-final-sets { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; margin-top: 2px; color: #cbd5e1; font-size: var(--font-size-3xl); font-weight: 700; }
@keyframes vb-final-in { from { transform: scale(0.3) rotate(-6deg); opacity: 0; } to { transform: scale(1) rotate(0); opacity: 1; } }
@keyframes vb-trophy { 0%, 100% { transform: translateY(0) rotate(0); } 25% { transform: translateY(-3px) rotate(-6deg); } 75% { transform: translateY(-3px) rotate(6deg); } }
.vb-final-enter-active { transition: opacity 0.3s; }
.vb-final-enter { opacity: 0; }
.arena-logo { opacity: 0.95; pointer-events: none; }
</style>
